import { baseApi } from "./baseApi";
import type { AnnouncementIntent, AnnouncementStatus } from "../../../shared/announcements";

/**
 * Feature A Inc-1 — admin announcement banner data layer (STATUS-announcements
 * §6). The admin list + CRUD are admin-gated server-side (requireCrmRole("admin"));
 * the /active feed is any-role and is consumed by the Inc-2 app-top banner.
 * instanceId is passed EXPLICITLY (cache-key convention, mirrors crmScoringConfigApi).
 */

/**
 * A full stored announcement row with the server-derived status chip.
 * `community_ids` is the target SET (empty = instance-wide / All communities,
 * §0 A1). `can_manage` is server-computed (§0 A2b) — the editor renders
 * Edit/Delete only when true (unmanageable rows are read-only).
 */
export interface AnnouncementListItem {
  id: string;
  community_ids: string[];
  title: string;
  body: string;
  link_url: string | null;
  link_label: string | null;
  intent: AnnouncementIntent;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  dismissible: boolean;
  created_by_person_id: string | null;
  updated_by_person_id: string | null;
  created_at: string;
  updated_at: string;
  status: AnnouncementStatus;
  can_manage: boolean;
}

/** The active-feed projection (Inc-2 banner). No community field — the banner never used it. */
export interface ActiveAnnouncement {
  id: string;
  title: string;
  body: string;
  link_url: string | null;
  link_label: string | null;
  intent: AnnouncementIntent;
  dismissible: boolean;
  updated_at: string;
}

/** Create/update body — matches the server parseWriteBody shape (§0 A1 target set). */
export interface AnnouncementWriteBody {
  community_ids: string[];
  title: string;
  body: string;
  link_url: string | null;
  link_label: string | null;
  intent: AnnouncementIntent;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  dismissible: boolean;
}

export const crmAnnouncementsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listAnnouncements: builder.query<
      { announcements: AnnouncementListItem[] },
      { instanceId: string }
    >({
      query: ({ instanceId }) => ({ url: "/crm/announcements", params: { instanceId } }),
      providesTags: [{ type: "CrmAnnouncements" as const, id: "LIST" }],
    }),

    getActiveAnnouncements: builder.query<
      { announcements: ActiveAnnouncement[] },
      { instanceId: string }
    >({
      query: ({ instanceId }) => ({ url: "/crm/announcements/active", params: { instanceId } }),
      providesTags: [{ type: "CrmAnnouncements" as const, id: "ACTIVE" }],
    }),

    createAnnouncement: builder.mutation<
      { announcement: AnnouncementListItem },
      { instanceId: string; body: AnnouncementWriteBody }
    >({
      query: ({ instanceId, body }) => ({
        url: "/crm/announcements",
        method: "POST",
        params: { instanceId },
        body,
      }),
      invalidatesTags: [
        { type: "CrmAnnouncements" as const, id: "LIST" },
        { type: "CrmAnnouncements" as const, id: "ACTIVE" },
      ],
    }),

    updateAnnouncement: builder.mutation<
      { announcement: AnnouncementListItem },
      { instanceId: string; id: string; body: AnnouncementWriteBody }
    >({
      query: ({ instanceId, id, body }) => ({
        url: `/crm/announcements/${id}`,
        method: "PATCH",
        params: { instanceId },
        body,
      }),
      invalidatesTags: [
        { type: "CrmAnnouncements" as const, id: "LIST" },
        { type: "CrmAnnouncements" as const, id: "ACTIVE" },
      ],
    }),

    deleteAnnouncement: builder.mutation<{ deleted: boolean }, { instanceId: string; id: string }>({
      query: ({ instanceId, id }) => ({
        url: `/crm/announcements/${id}`,
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: [
        { type: "CrmAnnouncements" as const, id: "LIST" },
        { type: "CrmAnnouncements" as const, id: "ACTIVE" },
      ],
    }),
  }),
});

export const {
  useListAnnouncementsQuery,
  useGetActiveAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
} = crmAnnouncementsApi;
